import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Spinner } from '@/components/Elements';
import { CouponForm } from '@/components/Elements/CouponForm';
import { tinyModalStyles, wideNoScrollModalStyles } from '@/components/Elements/Modal/customStyles';
import { UpdateProductCard } from '@/components/Elements/UpdateProductCard';
import { BILLING_MODE_UNIQUE, REQUEST_FAILED, REQUEST_SUCCESS } from '@/config/appConstants';
import { PaymentIntent } from '@/features/payment';
import { Currency, IntervalBillingMode, InvoicePreviewTypes, ProductTypes } from '@/types';
import { getBillingModeByIntervalCount, isPrepaidProduct } from '@/utils/helpers';

import { ErrorModal } from '../ErrorModal';
import { InvoiceUpdateSummary } from '../InvoiceUpdateSummary';
import { PaymentsModal } from '../PaymentsModal';

export type SummaryPaymentModalProps = {
  isOpen?: boolean;
  onRequestClose: () => void;
  onActionButton: (promotionCode: string) => void;
  newUserProduct: ProductTypes;
  newCurrency: Currency;
  newIntervalCount: IntervalBillingMode;
  isCouponEnabled?: boolean;
  onDemoCouponFormSubmit?: (values: any) => Promise<any>;
  onCouponFormSubmit?: (values: any) => Promise<any>;
  isDemo?: boolean;
  actionButtonLabel?: string;
  initialInvoiceUpdateSummaryData?: InvoicePreviewTypes;
  onSubmitCoupon?: (
    currency: Currency,
    product: ProductTypes | null,
    promotionCode: string | ''
  ) => Promise<void>;
  getCreatePaymentIntent?: (
    productSelected: ProductTypes | null | undefined,
    userCurrency: Currency,
    intervalCount: IntervalBillingMode,
    preview: boolean,
    promotionCode: string,
    onErrorCallback: (status: number) => void
  ) => Promise<PaymentIntent | undefined>;
};

export const SummaryPaymentModal = ({
  onRequestClose,
  onActionButton,
  isOpen = false,
  newUserProduct,
  newCurrency,
  newIntervalCount,
  isDemo,
  isCouponEnabled,
  onDemoCouponFormSubmit,
  onSubmitCoupon,
  initialInvoiceUpdateSummaryData,
  actionButtonLabel,
  getCreatePaymentIntent,
}: SummaryPaymentModalProps) => {
  const { t } = useTranslation();
  const [invoiceUpdateSummaryData, setInvoiceUpdateSummaryData] = useState<any | null>(
    initialInvoiceUpdateSummaryData
  );

  const getInitialCoupon = () => {
    if (initialInvoiceUpdateSummaryData) {
      return initialInvoiceUpdateSummaryData?.discounts?.[0]?.coupon?.id || '';
    }

    return '';
  };

  const getInitialCouponStatus = () => {
    return getInitialCoupon() ? REQUEST_SUCCESS : undefined;
  };

  const [couponStatus, setCouponStatus] = useState<any | null>(getInitialCouponStatus());
  const [isSummaryDataLoading, setIsSummaryDataLoading] = useState(false);
  const [currentCoupon, setCurrentCoupon] = useState<string>(getInitialCoupon());
  const [lastValidCoupon, setLastValidCoupon] = useState<string | undefined>(undefined);
  const [showSpinner, setShowSpinner] = useState(false);
  const [statusPaymentError, setStatusPaymentError] = useState<number>(0);

  const onDefaultActionButton = () => {
    onDefaultRequestClose();
    setShowSpinner(true);
    onActionButton && onActionButton(currentCoupon);
  };

  const onDefaultRequestClose = () => {
    onRequestClose && onRequestClose();
  };

  const renderSpinner = () => (
    <PaymentsModal
      closeButton={false}
      isOpen={showSpinner}
      style={tinyModalStyles}
      shouldCloseOnOverlayClick={false}
    >
      <div className="flex items-center justify-center">
        <Spinner size="xl" />
      </div>
    </PaymentsModal>
  );

  const onErrorCallback = (status: number) => {
    if (status === 503 || status === 500 || status === 402) return setStatusPaymentError(status);
    setCouponStatus(REQUEST_FAILED);
    setIsSummaryDataLoading(false);
  };

  const onDefaultCouponFormSubmit = async (values: any) => {
    const preview = true;
    setIsSummaryDataLoading(true);
    setCurrentCoupon(values.customCoupon);

    let promise;

    if (isDemo && onDemoCouponFormSubmit) {
      promise = onDemoCouponFormSubmit(values);
    } else if (onSubmitCoupon) {
      promise = onSubmitCoupon(newCurrency, newUserProduct, values.customCoupon);
    } else {
      if (!getCreatePaymentIntent) {
        return;
      }
      promise = getCreatePaymentIntent(
        newUserProduct,
        newCurrency,
        newIntervalCount,
        preview,
        values.customCoupon,
        onErrorCallback
      );
    }
    return promise.then((response) => {
      setIsSummaryDataLoading(false);

      if (response) {
        setCouponStatus(REQUEST_SUCCESS);
        setInvoiceUpdateSummaryData(response);
        setLastValidCoupon(values.customCoupon);
      }
    });
  };

  const getInvoiceUpdateSummaryStyle = () => {
    if (isCouponEnabled) {
      if (couponStatus === REQUEST_SUCCESS) {
        return 'summary__update--success';
      } else if (couponStatus === REQUEST_FAILED) {
        return 'summary__update--error';
      }
    }

    return '';
  };

  const isANewProductToUpdate = (product: ProductTypes) => {
    return !isPrepaidProduct(product);
  };

  const renderStatusPaymentErrorModal = () => (
    <ErrorModal status={statusPaymentError} onRequestClose={onDefaultRequestClose} />
  );

  return (
    <div>
      {!invoiceUpdateSummaryData && renderSpinner()}
      {statusPaymentError > 0 && renderStatusPaymentErrorModal()}

      {!showSpinner && (
        <PaymentsModal
          isOpen={isOpen}
          style={wideNoScrollModalStyles}
          onRequestClose={onDefaultRequestClose}
        >
          <div className="summary__payment__modal modal__content__wrapper max-h-[82vh] overflow-auto">
            <h2 className="md__payments__modal__title--h2">
              {t('ConfirmNeededPaymentModal.newProductInvoice')}
            </h2>

            <div className="md__payments__modal__banner">
              <UpdateProductCard
                product={newUserProduct}
                currency={newCurrency}
                billingMode={
                  isPrepaidProduct(newUserProduct)
                    ? BILLING_MODE_UNIQUE
                    : getBillingModeByIntervalCount(newIntervalCount)
                }
                isANewProductToUpdate={isANewProductToUpdate(newUserProduct)}
              />
            </div>

            {invoiceUpdateSummaryData && (
              <div className="md__payments__modal__summary">
                <InvoiceUpdateSummary
                  invoiceUpdateSummaryData={invoiceUpdateSummaryData}
                  isLoading={isSummaryDataLoading}
                  className={getInvoiceUpdateSummaryStyle()}
                />
              </div>
            )}

            {isCouponEnabled && (
              <CouponForm
                className="md__payments__modal__coupon"
                couponStatus={couponStatus}
                onSubmit={onDefaultCouponFormSubmit}
                currentCoupon={currentCoupon}
                lastValidCoupon={lastValidCoupon}
                isLoading={isSummaryDataLoading}
              />
            )}

            <div className="md__payments__modal__footer">
              <Button
                className="w-full"
                variant="primary"
                size="free"
                type="button"
                title={actionButtonLabel || t('ConfirmNeededPaymentModal.actionButton')}
                onClick={onDefaultActionButton}
                spinnerPlacement="start"
                startIcon={<i className="ri-shopping-bag-line" aria-hidden="true" />}
              >
                {actionButtonLabel || t('ConfirmNeededPaymentModal.actionButton')}
              </Button>
            </div>
          </div>
        </PaymentsModal>
      )}
    </div>
  );
};
